import { ApiErrorResponse, PaginatedResponse } from "@/redux/types";
import { SurveyViewAnswer } from "@/redux/survey-views/types";
import { AnswerResponse } from "@/redux/survey/types";
import { isPaginatedResponse } from "@/redux/typeGuards";
import { SurveyViewFormAnswer } from "@/features/survey-views/form/types";

export const toAnswerIds = (
  data: AnswerResponse[] | PaginatedResponse<AnswerResponse> | undefined,
): Set<number> => {
  if (!data) return new Set();
  const answers = isPaginatedResponse(data) ? data.results : data;
  return new Set(answers.map((answer) => answer.id));
};

/**
 * A saved survey view keeps a reference to every answer it shows. The answer is
 * gone as soon as its last evidence is removed, so a form that was loaded before
 * that happened still carries an id the API will reject.
 */
const STALE_ANSWER_ATTR = /^answers\.\d+\.answer$/;

export const hasStaleAnswerError = (error: unknown): boolean => {
  const errors = (error as ApiErrorResponse | undefined)?.data?.errors;
  if (!Array.isArray(errors)) return false;

  return errors.some(
    (item) =>
      item?.code === "does_not_exist" &&
      STALE_ANSWER_ATTR.test(item?.attr ?? ""),
  );
};

/**
 * Drops the answers that no longer exist, so a deleted one is never shown as
 * selected and never sent back on save. A `null` set means the live answers
 * could not be read, in which case nothing is dropped.
 */
export const toFormAnswers = (
  answers: SurveyViewAnswer[],
  existingAnswerIds: Set<number> | null,
): SurveyViewFormAnswer[] =>
  answers
    .filter(
      (answer) => !existingAnswerIds || existingAnswerIds.has(answer.answer),
    )
    .map((answer) => ({
      answer: answer.answer,
      question: answer.question,
      attachedBlocks: answer.attachedBlocks.map((block) => ({
        id: block.id,
        sharingRights: block.sharingRights,
      })),
    }));
